// src/components/AccountsListPage.jsx
// Full CRUD — Create, Read, Delete with cascade. Type-aware card layouts.

import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../lib/api';
import './AccountsListPage.css';
import DataTransferMenu from './DataTransferMenu';
// P0.31 31.A.2 — the ladder moved to src/lib so AccountDetailPage can offer a
// legal status when an account's TYPE changes, instead of a third hardcoded copy.
import { STATUS_OPTIONS } from '../lib/accountLadder';
// P0.31 31.B.4/31.B.6 — the shared confirm for an account that still has children.
import { confirmArchiveWithChildren } from '../lib/archiveConfirm';
// P0.31 31.B.5 — the Archived view. 7 accounts were archived and unreachable.
import useArchivedView from '../hooks/useArchivedView';

// P0.21 task 21.6c. Four RELATIONSHIP SHAPES, not industries. 'Carrier' named
// an insurance company that issues policies; the BEHAVIOUR it gated — tier
// badge, AI signals, research queue, the 8-stage sales ladder — is what any
// tenant does with an account it sells TO.
//
// Must stay in step with VOCAB.accountType in api/shared/import-schema.js. The
// CRA build cannot import from api/, so the duplication is structural;
// tests/account-status-ladder.test.js asserts the two agree.
const ACCOUNT_TYPES = ['Customer', 'Competitor', 'Vendor', 'Partner'];

// The type an account gets when nobody chose one. Mirrors DEFAULT_ACCOUNT_TYPE
// in api/shared/import-schema.js — the API stamps it on read, and this is the
// frontend half of the same decision.
const DEFAULT_ACCOUNT_TYPE = 'Customer';

const ACCOUNT_TYPE_CONFIG = {
  'Customer':        { icon: '🏢', color: '#2F5BFF', bg: '#2F5BFF12', label: 'Customer' },
  'Competitor':      { icon: '👥', color: 'var(--dm-green-soft)', bg: '#10B98112', label: 'Competitor' },
  'Vendor':          { icon: '⚙️', color: 'var(--dm-purple-mid)', bg: '#8B5CF612', label: 'Vendor' },
  'Partner':         { icon: '🤝', color: '#F59E0B', bg: '#F59E0B12', label: 'Partner' },
};


const TIER_OPTIONS = ['Tier 1','Tier 2','Tier 3','Tier 4'];

const STATUS_COLOR = {
  'Targeted':'var(--dm-grey-600)','Under Review':'var(--dm-grey-500)','Engaged':'var(--dm-blue-mid)','Active':'var(--dm-green-soft)',
  'Stalled':'#f59e0b','Closed Won':'var(--dm-green)','Closed Tabled by Client':'#f97316',
  'Closed Lost to Competitor':'#ef4444','Active Partner':'var(--dm-green-soft)','Evaluating':'var(--dm-blue-mid)',
  'Prospecting':'var(--dm-indigo-soft)','Inactive':'var(--dm-grey-500)',
};

const TIER_COLOR = { 'Tier 1':'#ef4444','Tier 2':'#f59e0b','Tier 3':'var(--dm-purple-mid)','Tier 4':'#ec4899' };

const RELATIONSHIP_BORDER = {
  'Strong — Executive Access':  '#16a34a',
  'Developing — Working Level': 'var(--dm-blue-vivid)',
  'Weak — Limited Contact':     '#f59e0b',
  'None — Net New':             'var(--dm-grey-400)',
};

const BLANK_FORM = {
  accountName: '', accountType: DEFAULT_ACCOUNT_TYPE, tier: '', accountStatus: '',
  companyWebsite: '', linkedIn: '', annualRevenue: '', employeeCount: '',
  companyOverview: '',
};

function AccountsListPage({ onSelectAccount }) {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTier, setFilterTier] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterType, setFilterType] = useState('');
  const [queuedIds, setQueuedIds] = useState({});
  const [counts, setCounts] = useState({});

  // Create modal
  const [showCreate, setShowCreate] = useState(false);
  const [formData, setFormData] = useState(BLANK_FORM);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // Delete confirm
  const [deleteTarget, setDeleteTarget] = useState(null); // { id, accountName }
  const [deleting, setDeleting] = useState(false);

  const archive = useArchivedView();

  const fetchAccounts = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filterTier) params.append('tier', filterTier);
      if (filterStatus) params.append('status', filterStatus);
      const response = await apiFetch(`/api/accounts/list?${params.toString()}${archive.param}`);
      const data = await response.json();
      if (data.success) setAccounts(data.accounts);
      else setError(data.error || 'Failed to load accounts');
    } catch (err) {
      setError('Failed to fetch accounts');
    } finally {
      setLoading(false);
    }
  }, [filterTier, filterStatus, archive.param]);

  useEffect(() => { fetchAccounts(); }, [fetchAccounts]);

  // Batch-load all counts in a single request — Supabase is fast enough for this
  useEffect(() => {
    if (accounts.length === 0) return;
    let cancelled = false;
    const go = async () => {
      try {
        const res  = await apiFetch('/api/accounts/counts');
        const data = await res.json();
        if (data.success && data.counts && !cancelled) {
          setCounts(data.counts);
        }
      } catch { /* silent */ }
    };
    go();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accounts]);

  const filteredAccounts = accounts.filter(a => {
    const matchName = a.accountName?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchType = !filterType || a.accountType === filterType;
    return matchName && matchType;
  });

  // ── Queue for research ──────────────────────────────────────────────────────
  const handleQueue = async (e, account) => {
    e.stopPropagation();
    if (queuedIds[account.id]) return;
    setQueuedIds(prev => ({ ...prev, [account.id]: 'queuing' }));
    try {
      const res = await apiFetch('/api/research/queue', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ accountId: account.id, accountName: account.accountName }),
      });
      const data = await res.json();
      const status = data.success ? (data.results?.[0]?.status === 'already_queued' ? 'already_queued' : 'queued') : 'error';
      setQueuedIds(prev => ({ ...prev, [account.id]: status }));
    } catch {
      setQueuedIds(prev => ({ ...prev, [account.id]: 'error' }));
    }
  };

  const queueLabel = (id) => {
    const s = queuedIds[id];
    if (s === 'queuing') return '⏳';
    if (s === 'queued' || s === 'already_queued') return '✅ Queued';
    if (s === 'error') return '❌';
    return '🌙 Queue';
  };

  // ── Create account ──────────────────────────────────────────────────────────
  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCreate = async () => {
    if (!formData.accountName.trim()) { setSaveError('Account name is required'); return; }
    setSaving(true);
    setSaveError(null);
    try {
      const res = await apiFetch('/api/accounts/create', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      const data = await res.json();
      if (data.success) {
        setShowCreate(false);
        setFormData(BLANK_FORM);
        await fetchAccounts();
        if (data.accountId && !data.alreadyExists && onSelectAccount) {
          onSelectAccount(data.accountId);
        }
      } else {
        setSaveError(data.error || 'Failed to create account');
      }
    } catch (err) {
      setSaveError('Network error — please try again');
    } finally {
      setSaving(false);
    }
  };

  // ── Delete account ──────────────────────────────────────────────────────────
  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      // P0.31 — `id`, not `accountId`. api/accounts/delete.js reads `id`, so
      // Delete from this list has never worked: it returned "Account ID is
      // required" on every attempt. The endpoint now accepts both, and this sends
      // the name it documents.
      const res = await apiFetch('/api/accounts/delete', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: deleteTarget.id }),
      });
      const data = await res.json();
      if (data.success) {
        setDeleteTarget(null);
        await fetchAccounts();
      } else if (res.status === 409 && data.canForce) {
        // 31.B.4 — the account still has contacts or open opportunities. The
        // server refuses rather than orphaning them; the user is told what is in
        // the way and may insist.
        if (confirmArchiveWithChildren({
          entity: 'account', name: deleteTarget.accountName, blockedBy: data.blockedBy,
        })) {
          const forced = await apiFetch('/api/accounts/delete', {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: deleteTarget.id, force: true }),
          });
          const fdata = await forced.json();
          if (fdata.success) { setDeleteTarget(null); await fetchAccounts(); }
          else alert('Archive failed: ' + (fdata.error || 'unknown error'));
        }
      } else {
        alert('Delete failed: ' + data.error);
      }
    } catch (err) {
      alert('Network error during delete');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) return (
    <div className="accounts-list-page">
      <div className="loading-container"><div className="spinner"></div><p>Loading accounts...</p></div>
    </div>
  );

  if (error) return (
    <div className="accounts-list-page">
      <div className="error-container">
        <h2>Error Loading Accounts</h2><p>{error}</p>
        <button onClick={fetchAccounts} className="retry-button">Retry</button>
      </div>
    </div>
  );

  return (
    <div className="accounts-list-page">

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="page-header">
        <div className="header-left">
          <h1>All Accounts</h1>
          <span className="account-count">{filteredAccounts.length} accounts</span>
        </div>
        <div className="header-right">
          <DataTransferMenu entity="accounts" label="Accounts" onImported={fetchAccounts} />
          <button onClick={() => { setShowCreate(true); setSaveError(null); setFormData(BLANK_FORM); }} className="btn-create-account">
            + New Account
          </button>
          {/* P0.31 31.B.5 — the Archived view, the same control every entity has. */}
          <button onClick={archive.toggle}
                  className={`refresh-button${archive.showArchived ? ' refresh-button-on' : ''}`}
                  title={archive.button.title}>
            {archive.showArchived ? '← Active' : '🗄'}
          </button>
          <button onClick={fetchAccounts} className="refresh-button" title="Refresh">↻</button>
        </div>
      </div>

      {/* ── Filters ────────────────────────────────────────────────────────── */}
      <div className="page-controls">
        <input type="text" className="search-input" placeholder="Search by name..."
          value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
        <select value={filterType} onChange={(e) => setFilterType(e.target.value)} className="filter-select">
          <option value="">All Types</option>
          {ACCOUNT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <select value={filterTier} onChange={(e) => setFilterTier(e.target.value)} className="filter-select">
          <option value="">All Tiers</option>
          {TIER_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="filter-select">
          <option value="">All Statuses</option>
          {Object.values(STATUS_OPTIONS).flat().filter((v,i,a)=>a.indexOf(v)===i).map(s =>
            <option key={s} value={s}>{s}</option>
          )}
        </select>
      </div>

      {/* ── Account Grid ───────────────────────────────────────────────────── */}
      {filteredAccounts.length === 0 ? (
        <div className="no-accounts">
          <p>No accounts found{searchTerm && ` matching "${searchTerm}"`}.</p>
        </div>
      ) : (
        <div className="accounts-grid">
          {filteredAccounts.map(account => {
            const typeConfig = ACCOUNT_TYPE_CONFIG[account.accountType] || ACCOUNT_TYPE_CONFIG[DEFAULT_ACCOUNT_TYPE];
            // "Do we sell TO this account?" — the question every gate below is really
            // asking. A missing type counts, because the API defaults it the same way.
            const isCustomer = !account.accountType || account.accountType === 'Customer';
            return (
              <div key={account.id} className="account-card" onClick={() => onSelectAccount?.(account.id)}
                style={{
                  '--accent': typeConfig.color,
                  borderLeft: account.relationshipStrength
                    ? '3px solid ' + (RELATIONSHIP_BORDER[account.relationshipStrength] || 'var(--dm-grey-400)')
                    : undefined,
                }}>

                {/* Card Header */}
                <div className="account-card-header">
                  <div className="account-name-row">
                    <span className="account-type-icon">{typeConfig.icon}</span>
                    <h3 className="account-name">{account.accountName}</h3>
                  </div>
                  <div className="account-card-badges">
                    <span className="account-type-pill" style={{ color: typeConfig.color, background: typeConfig.bg }}>
                      {typeConfig.label}
                    </span>
                    {isCustomer && account.tier && (
                      <span className="tier-badge" style={{ backgroundColor: TIER_COLOR[account.tier] }}>
                        {account.tier}
                      </span>
                    )}
                    {account.relationshipStrength && account.relationshipStrength !== 'None — Net New' && (
                      <span className="rel-strength-pill" style={{
                        background: (RELATIONSHIP_BORDER[account.relationshipStrength] || 'var(--dm-grey-400)') + '20',
                        color: RELATIONSHIP_BORDER[account.relationshipStrength] || 'var(--dm-grey-600)',
                        border: '1px solid ' + (RELATIONSHIP_BORDER[account.relationshipStrength] || 'var(--dm-grey-400)'),
                      }}>
                        {account.relationshipStrength === 'Strong — Executive Access'  ? '● Strong' :
                         account.relationshipStrength === 'Developing — Working Level' ? '● Developing' :
                         account.relationshipStrength === 'Weak — Limited Contact'     ? '● Weak' : null}
                      </span>
                    )}
                  </div>
                </div>

                {/* Status */}
                {account.accountStatus && (
                  <div className="account-status" style={{ backgroundColor: STATUS_COLOR[account.accountStatus] || 'var(--dm-grey-600)' }}>
                    {account.accountStatus}
                  </div>
                )}

                {/* AI Summary — Carriers and Vendors */}
                {(isCustomer || account.accountType === 'Vendor') && account.aiSummary && (
                  <p className="account-summary">
                    {account.aiSummary.substring(0, 110)}{account.aiSummary.length > 110 ? '...' : ''}
                  </p>
                )}

                {/* Metrics row */}
                <div className="account-metrics">
                  {(() => {
                    const c   = counts[account.id];
                    const fmt = (v) => c ? v : <span className="metric-loading">…</span>;
                    if (isCustomer) return (<>
                      <div className="metric"><span className="metric-icon">💼</span><span className="metric-value">{fmt(c?.opportunityCount ?? 0)}</span><span className="metric-label">Opps</span></div>
                      <div className="metric"><span className="metric-icon">👥</span><span className="metric-value">{fmt(c?.contactCount ?? 0)}</span><span className="metric-label">Contacts</span></div>
                      <div className="metric"><span className="metric-icon">📡</span><span className="metric-value">{fmt(c?.signalCount ?? 0)}</span><span className="metric-label">Signals</span></div>
                    </>);
                    if (account.accountType === 'Competitor') return (<>
                      <div className="metric"><span className="metric-icon">👥</span><span className="metric-value">{fmt(c?.contactCount ?? 0)}</span><span className="metric-label">Team</span></div>
                      <div className="metric"><span className="metric-icon">💼</span><span className="metric-value">{fmt(c?.opportunityCount ?? 0)}</span><span className="metric-label">Active</span></div>
                    </>);
                    return (<>
                      <div className="metric"><span className="metric-icon">👤</span><span className="metric-value">{fmt(c?.contactCount ?? 0)}</span><span className="metric-label">Contacts</span></div>
                      <div className="metric"><span className="metric-icon">💼</span><span className="metric-value">{fmt(c?.opportunityCount ?? 0)}</span><span className="metric-label">Deals</span></div>
                    </>);
                  })()}
                </div>

                {/* AI Signals — Carriers only */}
                {isCustomer && account.aiSignals?.length > 0 && (
                  <div className="account-signals">
                    {account.aiSignals.slice(0,3).map((s,i) => <span key={i} className="signal-tag">{s}</span>)}
                  </div>
                )}

                {/* Card Footer */}
                <div className="account-card-footer">
                  {isCustomer && (
                    <button
                      className={`queue-button${queuedIds[account.id] === 'queued' || queuedIds[account.id] === 'already_queued' ? ' queue-button--done' : ''}${queuedIds[account.id] === 'error' ? ' queue-button--error' : ''}`}
                      onClick={(e) => handleQueue(e, account)}
                      disabled={!!queuedIds[account.id]}
                    >
                      {queueLabel(account.id)}
                    </button>
                  )}
                  <button className="view-button" onClick={(e) => { e.stopPropagation(); onSelectAccount?.(account.id); }}>
                    View Details →
                  </button>
                  <button className="delete-account-btn" title="Delete account"
                    onClick={(e) => { e.stopPropagation(); setDeleteTarget({ id: account.id, accountName: account.accountName }); }}>
                    🗑
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Create Account Modal ───────────────────────────────────────────── */}
      {showCreate && (
        <div className="modal-overlay" onClick={() => setShowCreate(false)}>
          <div className="modal-panel" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>New Account</h2>
              <button className="modal-close" onClick={() => setShowCreate(false)}>×</button>
            </div>
            <div className="modal-body">

              {saveError && <div className="modal-error">⚠️ {saveError}</div>}

              {/* Account Name */}
              <div className="form-group">
                <label>Account Name *</label>
                <input type="text" value={formData.accountName}
                  onChange={(e) => handleFormChange('accountName', e.target.value)}
                  placeholder="e.g., Donegal Insurance Group" autoFocus />
              </div>

              {/* Account Type — FIRST type-defining field */}
              <div className="form-group">
                <label>Account Type *</label>
                <div className="type-selector">
                  {ACCOUNT_TYPES.map(t => {
                    const cfg = ACCOUNT_TYPE_CONFIG[t];
                    return (
                      <button key={t}
                        className={`type-btn ${formData.accountType === t ? 'active' : ''}`}
                        style={formData.accountType === t ? { borderColor: cfg.color, background: cfg.bg, color: cfg.color } : {}}
                        onClick={() => handleFormChange('accountType', t)}>
                        {cfg.icon} {t}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Status */}
              <div className="form-row">
                <div className="form-group">
                  <label>Status</label>
                  <select value={formData.accountStatus} onChange={(e) => handleFormChange('accountStatus', e.target.value)}>
                    <option value="">Select status...</option>
                    {(STATUS_OPTIONS[formData.accountType] || STATUS_OPTIONS[DEFAULT_ACCOUNT_TYPE]).map(s =>
                      <option key={s} value={s}>{s}</option>
                    )}
                  </select>
                </div>

                {/* Tier — Carriers only */}
                {formData.accountType === 'Customer' && (
                  <div className="form-group">
                    <label>Tier</label>
                    <select value={formData.tier} onChange={(e) => handleFormChange('tier', e.target.value)}>
                      <option value="">Select tier...</option>
                      {TIER_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                )}
              </div>

              {/* Website + LinkedIn */}
              <div className="form-row">
                <div className="form-group">
                  <label>Website</label>
                  <input type="url" value={formData.companyWebsite}
                    onChange={(e) => handleFormChange('companyWebsite', e.target.value)}
                    placeholder="https://..." />
                </div>
                <div className="form-group">
                  <label>LinkedIn</label>
                  <input type="url" value={formData.linkedIn}
                    onChange={(e) => handleFormChange('linkedIn', e.target.value)}
                    placeholder="https://linkedin.com/company/..." />
                </div>
              </div>

              {/* Revenue + Employees — Carriers and Vendors */}
              {(formData.accountType === 'Customer' || formData.accountType === 'Vendor') && (
                <div className="form-row">
                  <div className="form-group">
                    <label>Annual Revenue</label>
                    <input type="text" value={formData.annualRevenue}
                      onChange={(e) => handleFormChange('annualRevenue', e.target.value)}
                      placeholder="e.g., $500M" />
                  </div>
                  <div className="form-group">
                    <label>Employee Count</label>
                    <input type="text" value={formData.employeeCount}
                      onChange={(e) => handleFormChange('employeeCount', e.target.value)}
                      placeholder="e.g., 2,500" />
                  </div>
                </div>
              )}

              {/* Overview */}
              <div className="form-group">
                <label>
                  {formData.accountType === 'Competitor' ? 'About this firm' :
                   formData.accountType === 'Vendor' ? 'Product / Service Overview' :
                   formData.accountType === 'Partner' ? 'Partnership Overview' :
                   'Company Overview'}
                </label>
                <textarea value={formData.companyOverview}
                  onChange={(e) => handleFormChange('companyOverview', e.target.value)}
                  rows={3} placeholder="Brief description..." />
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowCreate(false)} disabled={saving}>Cancel</button>
              <button className="btn-save-account" onClick={handleCreate} disabled={saving}>
                {saving ? 'Creating...' : `Create ${formData.accountType || 'Account'}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete Confirm Modal ───────────────────────────────────────────── */}
      {deleteTarget && (
        <div className="modal-overlay" onClick={() => !deleting && setDeleteTarget(null)}>
          <div className="modal-panel modal-panel--narrow" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Delete Account?</h2>
            </div>
            <div className="modal-body">
              <p>You are about to permanently archive <strong>{deleteTarget.accountName}</strong> and all of its associated records:</p>
              <ul className="delete-warning-list">
                <li>All Contacts</li>
                <li>All Leads</li>
                <li>All Opportunities</li>
                <li>All Strategic Signals</li>
                <li>All Activities</li>
                <li>All Tech Stack entries</li>
                <li>All Meetings</li>
              </ul>
              <p className="delete-confirm-note">This cannot be undone from the app. Are you sure?</p>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</button>
              <button className="btn-delete-confirm" onClick={handleDeleteConfirm} disabled={deleting}>
                {deleting ? 'Deleting...' : 'Yes, Delete Everything'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default AccountsListPage;
